/*
 * Purpose: AI analytics analyst — interpretation + recommendations
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useState } from 'react';
import { Bot, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContextualError } from '@/components/errors/ContextualError';
import { interpretAnalyticsInsight } from '@/lib/adminApi';
import type { AiAnalyticsInsight } from '@/lib/siteAnalyticsTypes';
import { APP_ROUTES } from '@/lib/appRoutes';
import { Link } from 'react-router-dom';

export function AiAnalyticsPanel() {
  const [insight, setInsight] = useState<AiAnalyticsInsight | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<{ label: string; href: string } | undefined>();

  const run = async () => {
    setLoading(true);
    setError(null);
    setErrorAction(undefined);
    try {
      const result = await interpretAnalyticsInsight(14);
      setInsight(result.insight);
    } catch (e) {
      const err = e as Error & { action?: { label: string; href: string } };
      setError(err.message);
      setErrorAction(err.action);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-[hsl(var(--ab-green-core))]" />
            AI analytics analyst
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Interprets site traffic, user activity, and error trends — requires OpenAI key in Secrets.
          </p>
        </div>
        <Button onClick={() => void run()} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-1.5" />
          )}
          Run analysis
        </Button>
      </div>

      {error && (
        <ContextualError
          message={error}
          action={
            errorAction
              ? { label: errorAction.label, href: errorAction.href }
              : { label: 'Retry', onClick: () => void run() }
          }
          secondaryAction={{ label: 'Connect secrets', href: APP_ROUTES.connectSecrets }}
        />
      )}

      {!insight && !loading && !error && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Click <strong>Run analysis</strong> to generate AI recommendations from the last 14 days.
          </CardContent>
        </Card>
      )}

      {insight && (
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>{insight.headline}</CardTitle>
              <CardDescription>{insight.summary}</CardDescription>
            </CardHeader>
          </Card>

          {insight.trends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Trends</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {insight.trends.map((t, i) => (
                  <p key={i} className="text-sm text-muted-foreground">
                    • {t}
                  </p>
                ))}
              </CardContent>
            </Card>
          )}

          {insight.recommendations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recommendations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {insight.recommendations.map((r, i) => (
                  <div key={i} className="rounded-lg border border-border/60 p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{r.title}</span>
                      <PriorityBadge priority={r.priority} />
                    </div>
                    <p className="text-sm text-muted-foreground">{r.detail}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {insight.riskFlags.length > 0 && (
            <Card className="border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-base text-amber-800 dark:text-amber-300">Risk flags</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {insight.riskFlags.map((f, i) => (
                  <p key={i} className="text-sm text-muted-foreground">
                    ⚠ {f}
                  </p>
                ))}
              </CardContent>
            </Card>
          )}

          <Button variant="outline" size="sm" asChild>
            <Link to={APP_ROUTES.adminAnalytics}>View raw analytics</Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: 'high' | 'medium' | 'low' }) {
  const cls =
    priority === 'high'
      ? 'bg-red-500/15 text-red-700 dark:text-red-300'
      : priority === 'medium'
        ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cls}>
      {priority}
    </Badge>
  );
}
